import { getUser } from "@/lib/auth";
import {
  customElementAttributeMappers,
  goToParentModal,
  setCustomElementMetadata,
} from "@/lib/customElements";
import { onFormInvalid } from "@/lib/form";
import { QuoteTemplateSchema } from "@/models";
import {
  fromApiModel,
  toApiModel,
} from "@/models/quote-template-line-item";
import { WORK_ORDER_TYPES } from "@/models/work-order";
import { MaterialService, QuoteTemplateService } from "@/services";
import { WarrantiesService } from "@/services/warranty";
import { Warranty } from "@/models/warranty";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Card, CardContent } from "../base/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../base/form";
import { Input } from "../base/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import { Skeleton } from "../base/skeleton";
import { MultiSelect } from "../controls/multi-select";
import { LineItem } from "@/models/line-item";
import { LineItemsGroup } from "./line-items-group";
import { MaterialFormula } from "@/models/material-formula";

const formSchema = QuoteTemplateSchema;

type FormData = z.infer<typeof formSchema>;

interface QuoteTemplateFormProps {
  apiUrl: string;
  apiStage: string;
  onSubmit?: (data: FormData) => void;
  initialData?: FormData;
  templateId?: number;
}

interface ErrorSplashProps {
  message: string;
}

function ErrorSplash({ message }: Readonly<ErrorSplashProps>) {
  return (
    <div className="flex gap-2 items-center p-4 rounded-lg bg-destructive/10 text-destructive">
      <AlertCircle className="w-5 h-5" />
      <p>{message}</p>
    </div>
  );
}

function debounceFormData(
  func: (data: FormData) => Promise<void>,
  wait: number
): (data: FormData) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (data: FormData) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(data);
      timeout = null;
    }, wait);
  };
}

export function QuoteTemplateForm({
  apiUrl = "",
  apiStage = "",
  onSubmit,
  initialData,
  templateId,
}: Readonly<QuoteTemplateFormProps>) {
  const quoteService = React.useMemo(
    () => new QuoteTemplateService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const warrantieService = React.useMemo(
    () => new WarrantiesService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
    const materialService = React.useMemo(
      () => new MaterialService(apiUrl, apiStage),
      [apiUrl, apiStage]
    );
    const contentRef = React.useRef<HTMLDivElement>(null);
    
    const [isLoading, setIsLoading] = React.useState(!!templateId);
    const [dataLoaded, setDataLoaded] = React.useState(!templateId);
    const [error, setError] = React.useState<string | null>(null);
    const [warranties, setWarranties] = React.useState<Warranty[]>([]);
    const [isAutoSaving, setIsAutoSaving] = React.useState(false);
    const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const [materialFormulas, setMaterialFormulas] = React.useState<
    MaterialFormula[]
  >([]);  
  const user = getUser();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData || {
      nttitle: "",
      ntmarket: "",
      ntaccess: 0,
      ntview: "",
      ntwotype: null,
      lineItems: [],
      nttype: null,
      ntcat: null,
      ntdetail: null,
      ntlineitems: null,
    },
    shouldFocusError: false,
  });

  const fetchQuoteTemplate = React.useCallback(async () => {
    if (!templateId) return;

    setIsLoading(true);
    setDataLoaded(false);
    setError(null);
    try {
      const quoteTemplate = await quoteService.getQuoteTemplateById(templateId);
      form.reset(quoteTemplate);
      setDataLoaded(true);
    } catch (error) {
      console.error("Failed to get quote template:", error);
      setError("Failed to load quote template. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [templateId, quoteService, form]);

  React.useEffect(() => {
    fetchQuoteTemplate();
  }, [fetchQuoteTemplate]);

  React.useEffect(() => {
    const fetchWarranties = async () => {
      try {
        const warranties = await warrantieService.getAllWarranties();
        setWarranties(warranties);
      } catch (error) {
        console.error("Failed to fetch warranties:", error);
      }
    };
    fetchWarranties();
  }, [warrantieService]);


    // Add effect to fetch material formulas
    React.useEffect(() => {
      const fetchMaterialFormulas = async () => {
        try {
          const formulas = await materialService.getAllMaterialFormulas();
          setMaterialFormulas(formulas);
        } catch (error) {
          console.error("Failed to fetch material formulas:", error);
        }
      };
      fetchMaterialFormulas();
    }, [materialService]);

  // Convert backend model to frontend model
  const handleLineItemsChange = React.useCallback((lineItems: LineItem[]) => {
    // Convert LineItem[] to template line items
    const templateLineItems = lineItems.map(item => {
      return toApiModel({
        id: item.id,
        trackingId: item.trackingId,
        type: item.type,
        title: item.title,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unitCost: item.unitCost,
        cost: item.cost,
        markup: item.markup,
        applyTax: item.applyTax,
        materialTypeId: item.materialTypeId,
        materialStyleId: item.materialStyleId,
        materialSupplierId: item.materialSupplierId,
        order: item.order,
        groupName: item.groupName,
        date: item.date,
        typeId: item.typeId,
        useQuoteFile: item.useQuoteFile || false,
        useClaimFile: item.useClaimFile || false,
        useWorkOrderFile: item.useWorkOrderFile || false,
        accepted: item.accepted || false,
        initials: item.initials,
        reason: item.reason,
        reasonNotes: item.reasonNotes,
        materialStyle: item.materialStyle,
        appliedFormulaId: item.appliedFormulaId,
        laborCategoryId: item.laborCategoryId,
        laborTypeId: item.laborTypeId,
      });
    });
    
    form.setValue("lineItems", templateLineItems);
  }, [form]);

  // Convert backend model to frontend model
  const getLineItems = React.useCallback((): LineItem[] => {
    return (form.getValues("lineItems") || []).map(item => {
      return fromApiModel(item);
    });
  }, [form]);

  const saveQuoteTemplate = React.useCallback(
    async (data: FormData) => {
      if (!templateId || !dataLoaded) return;

      setIsAutoSaving(true);
      try {
        const ntlineitems = data.lineItems?.length > 0 ? 1 : 0;
        await quoteService.updateQuoteTemplate(templateId, {
          ...data,
          ntid: templateId,
          ntlineitems,
        });
        setLastSavedAt(new Date());
      } catch (error) {
        console.error("Failed to auto-save quote template:", error);
      } finally {
        setIsAutoSaving(false);
      }
    },
    [templateId, quoteService, dataLoaded]
  );

  const debouncedSave = React.useMemo(
    () => debounceFormData(saveQuoteTemplate, 500),
    [saveQuoteTemplate]
  );

  const onFormSubmit = async (data: FormData) => {
    try {
      const ntlineitems = data.lineItems?.length > 0 ? 1 : 0;
      if (templateId) {
        await quoteService.updateQuoteTemplate(templateId, {
          ...data,
          ntid: templateId,
          ntlineitems,
        });
      } else {
        await quoteService.createQuoteTemplate({
          ...data,
          ntlineitems,
        });
      }
      onSubmit?.(data);
      goToParentModal("raes-quoteTemplates", "viewQuoteTemplates1");
    } catch (error) {
      console.error("Failed to save quote template:", error);
    }
  };

  React.useEffect(() => {
    if (!dataLoaded) return;

    const subscription = form.watch(() => {
      debouncedSave(form.getValues() as FormData);
    });

    return () => subscription.unsubscribe();
  }, [form, debouncedSave, dataLoaded]);

  const handleWorkTypeChange = (values: string[]) => {
    form.setValue("ntwotype", values.join(":"));
  };


  if (error) {
    return <ErrorSplash message={error} />;
  }

  if (isLoading) {
    return (
      <div className="w-full space-y-6 @container">
        <div className="grid grid-cols-1 gap-6 @md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="w-20 h-5" />
            <Skeleton className="w-full h-10" />
          </div>
          <div className="space-y-2">
            <Skeleton className="w-20 h-5" />
            <Skeleton className="w-full h-10" />
          </div>
          <div className="space-y-2">
            <Skeleton className="w-20 h-5" />
            <Skeleton className="w-full h-10" />
          </div>
          <div className="space-y-2">
            <Skeleton className="w-20 h-5" />
            <Skeleton className="w-full h-10" />
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="w-32 h-5" />
          <Skeleton className="w-full h-10" />
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Skeleton className="w-24 h-6" />
          </div>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <Skeleton className="w-full h-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onFormSubmit, (errors, event) => {
          onFormInvalid({ form, errors, event, container: contentRef.current })
        })}
        className="flex overflow-hidden relative flex-col w-full h-full"
      >
        <div className="flex sticky top-0 justify-between items-center py-2 border-b bg-background">
          <div></div>
          <div className="text-sm text-muted-foreground">
            {isAutoSaving ? (
              <span>Saving...</span>
            ) : lastSavedAt ? (
              <span>Last saved at {lastSavedAt.toLocaleTimeString()}</span>
            ) : null}
          </div>
        </div>
        <div
          ref={contentRef}
          className="flex-1 p-6 space-y-6 overflow-y-auto @container relative"
        >
          <div className="grid grid-cols-1 gap-6 @md:grid-cols-2">
            <FormField
              control={form.control}
              name="nttitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input required {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ntmarket"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Market</FormLabel>
                  <Select
                    required
                    {...field}
                    onValueChange={field.onChange}
                    defaultValue={field.value ?? undefined}
                    value={field.value ?? ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Market" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Both">
                        Commercial & Residential
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ntaccess"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Access</FormLabel>
                  <Select
                    required
                    {...field}
                    onValueChange={(value) => field.onChange(Number(value))}
                    defaultValue={String(field.value)}
                    value={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Access" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {user?.uroles === "Admin" && (
                        <SelectItem key={0} value="0">
                          All Users
                        </SelectItem>
                      )}
                      <SelectItem key={user?.uid} value={`${user?.uid}`}>
                        {user?.ufirstn} {user?.ulastn}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ntview"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Active</FormLabel>
                  <Select
                    required
                    {...field}
                    onValueChange={field.onChange}
                    defaultValue={field.value ?? undefined}
                    value={field.value ?? ""}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="ntwotype"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Work Order Types</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={[...WORK_ORDER_TYPES].sort().map((type) => ({
                      label: type,
                      value: type,
                    }))}
                    defaultValue={field.value ? field.value.split(":") : []}
                    onValueChange={handleWorkTypeChange}
                    placeholder="Select work order types"
                    maxCount={5}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-4 @xl:gap-2">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Line Items</h3>
            </div>

            <LineItemsGroup
              items={getLineItems()}
              onChange={handleLineItemsChange}
              workTypes={form.watch("ntwotype")?.split(":") || []}
              apiUrl={apiUrl}
              apiStage={apiStage}
              isTemplate
              warranties={warranties}
              sectionId={undefined}
              materialFormulas={materialFormulas}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}

QuoteTemplateForm.displayName = "QuoteTemplateForm";

setCustomElementMetadata({
  component: QuoteTemplateForm,
  tagName: "raes-quote-template-form",
  observedAttributes: {
    "api-url": (value: string) => ({ value, prop: "apiUrl" }),
    "api-stage": (value: string) => ({ value, prop: "apiStage" }),
    "template-id": (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "templateId",
    }),
  },
});
